import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { PermissionName, User } from '@src/app/model/userdata';
import { MatDialog } from '@angular/material/dialog';
import { EditUserComponent } from '@src/app/components/user-list/edit-user/edit-user.component';
import { PermissionsService } from '@src/app/services/permissions/permissions.service';
import { TranslateService } from '@ngx-translate/core';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { BehaviorSubject, Subject, takeUntil, throwError } from 'rxjs';
import { feSortToBeSort } from '@src/app/components/utils/utils';
import { Sort } from '@angular/material/sort';
import { JsonModal } from '@src/app/components/utils/json-modal';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  standalone: false,
})
export class UserListComponent implements OnInit, OnDestroy {
  public displayedColumns: string[] = ['id', 'email', 'action'];
  public currentUserItems: User[];
  public hasNextPage: boolean;
  public pageSize: number = 10;
  public currentPage: number = 0;
  public count: number = 0;
  public permissionUpdate = PermissionName.Update;
  public canEditUsers$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private _blueprintFailMsg: string = 'users.upload.failure';
  private _emailValidationFailMsg: string = 'users.upload.email';
  private _componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private _apolloService: ApolloService,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    public permissionsService: PermissionsService
  ) {
  }

  public ngOnInit(): void {
    this._apolloService.getAllUsers$()
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((usersAndPageData) => {
        this.currentUserItems = usersAndPageData.users;
        this.count = usersAndPageData.count;
        this.hasNextPage = usersAndPageData.hasNextPage;
      }
      );
    this.permissionsService.observePermission$('user', this.permissionUpdate).subscribe(this.canEditUsers$);
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
  }

  public paginate(toPage: number): void {
    this.currentPage = toPage;
    this._getUsers(this.pageSize, this.currentPage);
  }

  public setPageSize(newPageSize: string): void {
    // the new page size is a string because of the select component; if we somehow get a non-integer value, ignore it and reset the value.
    const pageSize = Number(newPageSize);
    if (pageSize > 0) {
      this.pageSize = pageSize;
      this.currentPage = 0;
      this._getUsers(this.pageSize, this.currentPage);
    } else {
      console.error('Received non-integer value for page size');
    }
  }

  public openEditDialog(user: User): void {
    this._dialog.open(EditUserComponent, {
      data: { user },
    });
  }

  public openUploadUserDialog($event: any, user?: User): void {
    $event.stopPropagation();
    // Email regex, based on the one in the backend
    // eslint-disable-next-line @stylistic/max-len
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_~\-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])+)+$/;
    const dialogRef = JsonModal.openJSONModalDialog(
      this._dialog,
      new JsonModalDataObject(
        'user',
        user || new User(),
        'users.upload.header',
        'buttons.submit',
        ['allRoles', 'systemRoles', 'password', 'totpQr', 'personalInformation']
      )
    );
    JsonModal.useJsonDialog(
      dialogRef,
      ((json) => {
        if (emailRegex.test(json?.user?.email)) {
          return this._apolloService.uploadUser$(json);
        } else {
          return throwError(() => new Error('bad email'));
        }
      }).bind(this._apolloService),
      this._translateService.instant(this._blueprintFailMsg),
      this._apolloService.getAllUsersRefresh.bind(this._apolloService),
      (_ => window.alert(this._translateService.instant(this._emailValidationFailMsg))).bind(this)
    );
  }

  private _getUsers(pageSize: number, currentPage: number, blocked?: boolean, sort?: Sort): void {
    const beSort = sort ? feSortToBeSort(sort) : undefined;
    this._apolloService.getAllUsers$(pageSize, pageSize * currentPage, blocked, beSort ? [beSort] : undefined)
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((usersAndPageData) => {
        this.currentUserItems = usersAndPageData.users;
        this.count = usersAndPageData.count;
        this.hasNextPage = usersAndPageData.hasNextPage;
      });
  }

  public sortChange(sortState: Sort): void {
    this._getUsers(this.pageSize, this.currentPage, false, sortState);
  }

  public getUsers(blocked: boolean): void {
    this._getUsers(this.pageSize, this.currentPage, blocked);
  }
}
